import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Select from "antd/es/select";
import cn from "classnames";
import { useIntl } from "react-intl";

import {
  AnyEntity,
  Company,
  Deal,
  EntityType,
  EntityTypesSupportingFilteringBy,
  Person,
} from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useChangeTracking from "@mapmycustomers/shared/util/hook/useChangeTracking";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { getApiService } from "@app/store/api";
import { getOrganizationId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import ApiService from "../../store/api/ApiService";

import styles from "./EntityMultiSelectField.module.scss";

interface Props {
  apiService?: ApiService;
  className?: string;
  disabled?: boolean;
  entityType: EntityTypesSupportingFilteringBy;
  onChange: (entityIds: Array<AnyEntity["id"]>) => void;
  onClick?: () => void;
  oneLiner?: boolean;
  organizationId?: Organization["id"];
  placeholder?: ReactNode;
  value: Array<AnyEntity["id"]>;
}

const EntityMultiSelectField = ({
  apiService,
  className,
  disabled,
  entityType,
  onChange,
  onClick,
  oneLiner,
  organizationId,
  placeholder,
  value,
}: Props) => {
  const intl = useIntl();
  const [entities, setEntities] = useState<(Company | Deal | Person)[]>([]);
  const [fetching, setFetching] = useState<boolean>(false);
  const [initialized, initialize] = useBoolean();

  const handleFetchEntities = useCallback(
    async (text: string, callback?: () => void) => {
      const searchText = text.trim();
      setFetching(true);

      const searchPayload = {
        $columns: ["id", "name"],
        $filters: {
          $and: [
            ...(searchText.length ? [{ name: { $in: searchText } }] : []),
            ...(value.length ? [{ id: { $nin: value } }] : []),
          ],
        },
        $limit: 100,
        $order: "name",
      };
      const searchResponse:
        | ListResponse<Company>
        | ListResponse<Deal>
        | ListResponse<Person>
        | undefined = await (
        entityType === EntityType.COMPANY
          ? apiService?.fetchCompanies
          : entityType === EntityType.PERSON
            ? apiService?.fetchPeople
            : apiService?.fetchDeals
      )?.(organizationId!, searchPayload);

      let response: ListResponse<Company> | ListResponse<Deal> | ListResponse<Person> | undefined =
        undefined;
      if (value.length) {
        const payload = {
          $columns: ["id", "name"],
          $filters: {
            $and: [...(value.length ? [{ id: { $in: value } }] : [])],
          },
          $limit: 100,
          $order: "name",
        };
        response = await (
          entityType === EntityType.COMPANY
            ? apiService?.fetchCompanies
            : entityType === EntityType.PERSON
              ? apiService?.fetchPeople
              : apiService?.fetchDeals
        )?.(organizationId!, payload);
      }

      setFetching(false);
      setEntities([...(searchResponse?.data ?? []), ...(response?.data ?? [])]);
      callback?.();
    },
    [apiService, entityType, organizationId, setFetching, value]
  );

  useEffect(() => {
    if (!initialized) {
      handleFetchEntities("", initialize);
    }
  }, [handleFetchEntities, initialize, initialized]);

  useChangeTracking(() => {
    handleFetchEntities("");
  }, [handleFetchEntities, value]);

  const handleSearch = useDebouncedCallback(
    [
      async (text: string) => {
        handleFetchEntities(text);
      },
      500,
    ],
    [handleFetchEntities]
  );

  if (!initialized) {
    return <LoadingSpinner mini />;
  }

  return (
    <Select<Array<AnyEntity["id"]>>
      className={cn(styles.container, className)}
      disabled={disabled}
      filterOption={defaultFilterOption}
      maxTagCount={oneLiner ? "responsive" : undefined}
      mode="multiple"
      notFoundContent={fetching ? <LoadingSpinner /> : null}
      onChange={onChange}
      onClick={onClick}
      onSearch={handleSearch}
      placeholder={
        placeholder ??
        intl.formatMessage({
          id: "entityMultiSelectField.placeholder",
          defaultMessage: "Click or type to select ",
          description: "Placeholder displayed in a EntityMultiSelectField component",
        })
      }
      value={value}
    >
      {entities.map((entity) => (
        <Select.Option key={entity.id} label={entity.name} value={entity.id}>
          {entity.name}
        </Select.Option>
      ))}
      {entities.length && fetching ? (
        <div key="loader">
          <LoadingSpinner />
        </div>
      ) : null}
    </Select>
  );
};

const mapStateToProps = (state: RootState) => ({
  apiService: getApiService(state),
  organizationId: getOrganizationId(state),
});

export default connect(mapStateToProps)(EntityMultiSelectField);
